import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/apiClient';

interface StepAnalytics {
  stepId: string;
  stepOrder: number;
  channel: string;
  sent: number;
  failed: number;
  scheduled: number;
  opens: number;
  clicks: number;
  replies: number;
  exits: number;
}

interface SequenceAnalytics {
  sequenceId: string;
  stepMetrics: StepAnalytics[];
}

export function SequenceAnalyticsPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState<SequenceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    apiClient.get<SequenceAnalytics>(`/api/v1/sequences/${id}/analytics`)
      .then((res) => setAnalytics(res.data))
      .catch(() => setError(t('sequences.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) return <p className="p-6 text-muted-foreground">{t('common.loading')}</p>;
  if (error || !analytics) return <p className="p-6 text-destructive">{error}</p>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Breadcrumbs crumbs={[
        { label: t('sequences.title'), to: '/admin/sequences' },
        { label: t('sequences.analytics.title') },
      ]} />

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/sequences">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('sequences.back')}
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">{t('sequences.analytics.title')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('sequences.analytics.subtitle')}</p>
        </div>
      </div>

      {analytics.stepMetrics.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('sequences.empty')}</p>
      ) : (
        <div className="space-y-3">
          {analytics.stepMetrics.map((step) => (
            <div key={step.stepId} className="border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold">
                  {step.stepOrder + 1}
                </span>
                <span className="font-medium text-sm">
                  {t(`sequences.channel.${step.channel}`)}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3 sm:grid-cols-7">
                {[
                  { label: t('sequences.analytics.sent'), value: step.sent, className: 'text-blue-600' },
                  { label: t('sequences.analytics.opens'), value: step.opens, className: 'text-green-600' },
                  { label: t('sequences.analytics.clicks'), value: step.clicks, className: 'text-indigo-600' },
                  { label: t('sequences.analytics.replies'), value: step.replies, className: 'text-purple-600' },
                  { label: t('sequences.analytics.exits'), value: step.exits, className: 'text-orange-600' },
                  { label: t('sequences.analytics.failed'), value: step.failed, className: 'text-red-600' },
                  { label: 'Scheduled', value: step.scheduled, className: 'text-muted-foreground' },
                ].map(({ label, value, className }) => (
                  <div key={label} className="text-center">
                    <div className={`text-xl font-bold ${className}`}>{value}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
